<rt-dialog-header
  [heading]="'tlc.positions.import_skills_flow.upload_file.heading' | translate"
  [subheading]="'tlc.positions.import_skills_flow.upload_file.subheading' | translate"
/>

<rt-dialog-body variant="white" [class.is-applying]="isApplying()">
  @if (isApplying()) {
    <div class="loading-container">
      <div class="loading-overlay">
        <rt-spinner variant="default" />
        <span class="loading-title">{{
          'tlc.positions.import_skills_flow.summary_dialog.loading.heading' | translate
        }}</span>
        <span>{{
          'tlc.positions.import_skills_flow.summary_dialog.loading.subheading' | translate
        }}</span>
      </div>
    </div>
  } @else {
    <tl-stepper [steps]="steps" />

    @if (messages().length > 0) {
      <div class="summary-section">
        <div class="text-container">
          <div class="section-heading">
            <rt-ai-logo size="small-x" />
            <span>{{
              'tlc.positions.import_skills_flow.summary_dialog.summary.heading' | translate
            }}</span>
          </div>
          @if (changeCounts().newSkills > 0) {
            <span class="skills-positions-count">{{
              'tlc.positions.import_skills_flow.summary_dialog.summary.changes.create_skills'
                | translate: { count: changeCounts().newSkills }
            }}</span>
          }
          @if (changeCounts().existingSkills > 0) {
            <span class="skills-positions-count">{{
              'tlc.positions.import_skills_flow.summary_dialog.summary.changes.update_skills'
                | translate: { count: changeCounts().existingSkills }
            }}</span>
          }
          @if (changeCounts().newPositions > 0) {
            <span class="skills-positions-count">{{
              'tlc.positions.import_skills_flow.summary_dialog.summary.changes.add_positions'
                | translate: { count: changeCounts().newPositions }
            }}</span>
          }
          @if (changeCounts().existingPositions > 0) {
            <span class="skills-positions-count">{{
              'tlc.positions.import_skills_flow.summary_dialog.summary.changes.assign_positions'
                | translate: { count: changeCounts().existingPositions }
            }}</span>
          }
          @if (skills().length > 0) {
            <span>{{
              'tlc.positions.import_skills_flow.summary_dialog.summary.analyzed_successfully'
                | translate
            }}</span>
            <span class="skills-analysis">{{
              'tlc.positions.import_skills_flow.summary_dialog.summary.skills_analysis' | translate
            }}</span>
          }
        </div>

        @if (messageGroups().positiveAndInfo.length > 0) {
          <div class="message-list">
            @for (message of messageGroups().positiveAndInfo; track $index) {
              <div class="message-item">
                <rt-icon
                  [name]="messageIconConfig[message.type].name"
                  [colorToken]="messageIconConfig[message.type].colorToken"
                  size="small"
                />
                <span class="message-text" [innerHTML]="message.sanitizedText"></span>
              </div>
            }
          </div>
        }
        @if (messageGroups().warning.length > 0) {
          <div class="attention-subsection">
            <span class="attention-heading">
              {{
                'tlc.positions.import_skills_flow.summary_dialog.summary.attention_heading'
                  | translate
              }}
            </span>
            <div class="message-list">
              @for (message of messageGroups().warning; track $index) {
                <div class="message-item">
                  <rt-icon
                    [name]="messageIconConfig[message.type].name"
                    [colorToken]="messageIconConfig[message.type].colorToken"
                    size="small"
                  />
                  <span class="message-text" [innerHTML]="message.sanitizedText"></span>
                </div>
              }
            </div>
          </div>
        }
      </div>
    }
    @for (message of messageGroups().blockingError; track $index) {
      <rt-infobox type="error" [isDismissible]="false" [heading]="message.text" />
    }

    @if (newPositions().length > 0) {
      <div class="new-positions-section">
        <rt-heading variant="heading bold"
          >{{ 'tlc.positions.import_skills_flow.summary_dialog.new_positions.heading' | translate }}
        </rt-heading>
        <span>{{
          'tlc.positions.import_skills_flow.summary_dialog.new_positions.subheading' | translate
        }}</span>
        <div class="position-tags">
          <div class="tags-container">
            @for (position of newPositions(); track $index) {
              <span class="position-tag">{{ position.name }}</span>
            }
          </div>
          <rt-group class="radio-group" variant="vertical stacked">
            <rt-radio
              [hasLensIcon]="true"
              [checked]="addNewPositions()"
              (click)="addNewPositions.set(true)"
            >
              {{
                'tlc.positions.import_skills_flow.summary_dialog.new_positions.add_option'
                  | translate
              }}
            </rt-radio>
            <rt-radio
              [hasLensIcon]="true"
              [checked]="!addNewPositions()"
              (click)="addNewPositions.set(false)"
            >
              {{
                'tlc.positions.import_skills_flow.summary_dialog.new_positions.skip_option'
                  | translate
              }}
            </rt-radio>
          </rt-group>
        </div>
      </div>
    }

    @if (skills().length > 0) {
      <div class="review-skills-section">
        <rt-heading variant="heading bold">
          {{ 'tlc.positions.import_skills_flow.summary_dialog.review_skills.heading' | translate }}
        </rt-heading>
        <span>
          {{
            'tlc.positions.import_skills_flow.summary_dialog.review_skills.subheading' | translate
          }}
        </span>

        @for (skill of skills(); track $index; let index = $index) {
          <tlc-imported-skill-card
            [skill]="skill"
            [groups]="groups()"
            [positions]="positions()"
            [excludeNewPositions]="!addNewPositions()"
            (skillSave)="onSkillSave(index, $event)"
            (proficiencySave)="onProficiencySave(index, $event)"
            (proficiencyDelete)="onProficiencyDelete(index, $event)"
            (importDecisionChange)="onImportDecisionChange(index, $event)"
            (collapsed)="onCardCollapsed(index)"
          />
        }
      </div>
    }
  }
</rt-dialog-body>

@if (!isApplying()) {
  <rt-dialog-footer [hasOptionalActions]="true">
    <div class="optional">
      <rt-button variant="plain" (click)="back()">
        <rt-icon name="arrow-back" />
        <span>{{ 'tlc.common.back' | translate }}</span>
      </rt-button>
    </div>
    <rt-button variant="primary" (click)="applyImport()">
      <rt-icon name="check" />
      <span>{{
        'tlc.positions.import_skills_flow.summary_dialog.import_and_apply_changes' | translate
      }}</span>
    </rt-button>
  </rt-dialog-footer>
}
